import { c as _c } from "react/compiler-runtime";
import { IcoChevronRight16, IcoSparkles16, IcoSparkles24 } from '@onefootprint/icons';
import { Button, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { Fragment, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilder } from '../../store/provider';
import type { SuggestionSection } from '../../store/suggestion-sections';
import type { Suggestion } from '../../store/types';
import { nodeName } from '../../store/types';
import ChangeRow from './change-row';
import { usePendingChanges } from './use-pending-changes';
type ChangesPanelProps = {
  onNavigate: (suggestion: Suggestion) => void;
};
const ChangesPanel = t0 => {
  const $ = _c(48);
  const {
    onNavigate
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.changes"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    acceptSuggestions,
    rejectSuggestions,
    isSavingGraph,
    isReadOnly
  } = useBuilder();
  const {
    sections,
    count,
    acceptableIds,
    dismissibleIds,
    chainedByParentId,
    orphans
  } = usePendingChanges();
  if (count === 0) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <EmptyState />;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <IcoSparkles16 color="accent" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== count || $[4] !== t) {
    t3 = count === 1 ? t(_temp) : t(_temp2, {
      count
    });
    $[3] = count;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className="min-w-0 truncate text-label-3 text-primary">{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = isSavingGraph || isReadOnly;
  let t6;
  if ($[8] !== dismissibleIds || $[9] !== rejectSuggestions) {
    t6 = () => rejectSuggestions(dismissibleIds);
    $[8] = dismissibleIds;
    $[9] = rejectSuggestions;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t(_temp3);
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = <LinkButton variant="label-3" disabled={t5} onClick={t6}>{t7}</LinkButton>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = acceptableIds.length === 0 || isSavingGraph || isReadOnly;
  let t10;
  if ($[17] !== acceptSuggestions || $[18] !== acceptableIds) {
    t10 = () => acceptSuggestions(acceptableIds);
    $[17] = acceptSuggestions;
    $[18] = acceptableIds;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t) {
    t11 = t(_temp4);
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = <Button variant="primary" size="compact" disabled={t9} onClick={t10}>{t11}</Button>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t8) {
    t13 = <div className="ml-auto flex items-center gap-3 shrink-0">{t8}{t12}</div>;
    $[26] = t12;
    $[27] = t8;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t4) {
    t14 = <div className="shrink-0 flex items-center gap-2 px-4 py-3 border-b border-tertiary">{t2}{t4}{t13}</div>;
    $[29] = t13;
    $[30] = t4;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== chainedByParentId || $[33] !== onNavigate || $[34] !== sections) {
    let t16;
    if ($[36] !== chainedByParentId || $[37] !== onNavigate) {
      t16 = section => <Section key={section.items[0].id} section={section} chainedByParentId={chainedByParentId} onNavigate={onNavigate} />;
      $[36] = chainedByParentId;
      $[37] = onNavigate;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    t15 = sections.map(t16);
    $[32] = chainedByParentId;
    $[33] = onNavigate;
    $[34] = sections;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[39] !== onNavigate || $[40] !== orphans) {
    t16 = orphans.length > 0 && <OrphanedSection orphans={orphans} onNavigate={onNavigate} />;
    $[39] = onNavigate;
    $[40] = orphans;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t15 || $[43] !== t16) {
    t17 = <div className="flex-1 min-h-0 overflow-y-auto pt-1 pb-24 md:py-1">{t15}{t16}</div>;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t14 || $[46] !== t17) {
    t18 = <div className="h-full flex flex-col">{t14}{t17}</div>;
    $[45] = t14;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
};
type SectionProps = {
  section: SuggestionSection;
  chainedByParentId: Map<string, Suggestion[]>;
  onNavigate: (suggestion: Suggestion) => void;
};
const Section = t0 => {
  const $ = _c(28);
  const {
    section,
    chainedByParentId,
    onNavigate
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.changes"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const label = useSectionLabel(section);
  let badge;
  if (section.kind === "node" && section.node) {
    if (section.node.kind === "rules") {
      let t2;
      if ($[1] !== t) {
        t2 = t(_temp5);
        $[1] = t;
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      badge = t2;
    } else {
      if (section.node.kind === "decision") {
        let t2;
        if ($[3] !== t) {
          t2 = t(_temp6);
          $[3] = t;
          $[4] = t2;
        } else {
          t2 = $[4];
        }
        badge = t2;
      } else {
        let t2;
        if ($[5] !== t) {
          t2 = t(_temp7);
          $[5] = t;
          $[6] = t2;
        } else {
          t2 = $[6];
        }
        badge = t2;
      }
    }
  }
  let t2;
  if ($[7] !== label) {
    t2 = <span className="text-label-3 text-tertiary uppercase tracking-wide truncate">{label}</span>;
    $[7] = label;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== badge) {
    t3 = badge && <span className="shrink-0 text-snippet-3 px-1 py-0.5 bg-secondary text-tertiary border border-tertiary rounded-sm">{badge}</span>;
    $[9] = badge;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <div className="flex items-center gap-2 px-4 pt-3 pb-1">{t2}{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== chainedByParentId || $[15] !== onNavigate || $[16] !== section.items || $[17] !== section.node) {
    let t6;
    if ($[19] !== chainedByParentId || $[20] !== onNavigate || $[21] !== section.node) {
      t6 = s => <Fragment key={s.id}><ChangeRow suggestion={s} node={section.node} onSelect={onNavigate} />{chainedByParentId.get(s.id)?.map(child => <ChangeRow key={child.id} suggestion={child} onSelect={onNavigate} nested={true} />)}</Fragment>;
      $[19] = chainedByParentId;
      $[20] = onNavigate;
      $[21] = section.node;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    t5 = section.items.map(t6);
    $[14] = chainedByParentId;
    $[15] = onNavigate;
    $[16] = section.items;
    $[17] = section.node;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[23] !== t5) {
    t6 = <ul className="flex flex-col">{t5}</ul>;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== t4 || $[26] !== t6) {
    t7 = <section className="mb-1">{t4}{t6}</section>;
    $[25] = t4;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
};
const useSectionLabel = section => {
  const $ = _c(14);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.changes.sections"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  if (section.kind === "inputs") {
    let t1;
    if ($[1] !== t) {
      t1 = t(_temp8);
      $[1] = t;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (section.kind === "outputs") {
    let t1;
    if ($[3] !== t) {
      t1 = t(_temp9);
      $[3] = t;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  if (section.kind === "norms") {
    let t1;
    if ($[5] !== t) {
      t1 = t(_temp10);
      $[5] = t;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  if (section.kind === "skills") {
    let t1;
    if ($[7] !== t) {
      t1 = t(_temp11);
      $[7] = t;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    return t1;
  }
  if (section.kind === "newStep") {
    let t1;
    if ($[9] !== t) {
      t1 = t(_temp12);
      $[9] = t;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    return t1;
  }
  let t1;
  if ($[11] !== section.node || $[12] !== t) {
    t1 = section.node && nodeName(section.node) || t(_temp13);
    $[11] = section.node;
    $[12] = t;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  return t1;
};
type OrphanedSectionProps = {
  orphans: Suggestion[];
  onNavigate: (suggestion: Suggestion) => void;
};

// Collapsed by default so out-of-date leftovers don't crowd the reviewable changes above.
const OrphanedSection = t0 => {
  const $ = _c(24);
  const {
    orphans,
    onNavigate
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.changes"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const [isExpanded, setIsExpanded] = useState(false);
  const listId = useId();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsExpanded(_temp14);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== orphans.length || $[3] !== t) {
    t3 = orphans.length === 1 ? t(_temp15) : t(_temp16, {
      count: orphans.length
    });
    $[2] = orphans.length;
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <span className="min-w-0 truncate">{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = isExpanded && "rotate-90";
  let t6;
  if ($[7] !== t5) {
    t6 = cx("ml-auto shrink-0 text-quaternary transition-transform", t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <IcoChevronRight16 className={t6} />;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== isExpanded || $[12] !== listId || $[13] !== t4 || $[14] !== t7) {
    t8 = <button type="button" onClick={t2} aria-expanded={isExpanded} aria-controls={listId} className="flex w-full items-center gap-2 px-4 py-2.5 text-body-3 text-tertiary cursor-pointer hover:bg-hover">{t4}{t7}</button>;
    $[11] = isExpanded;
    $[12] = listId;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== isExpanded || $[17] !== listId || $[18] !== onNavigate || $[19] !== orphans) {
    t9 = isExpanded && <ul id={listId} className="flex flex-col">{orphans.map(s => <ChangeRow key={s.id} suggestion={s} onSelect={onNavigate} hideStaleChip={true} />)}</ul>;
    $[16] = isExpanded;
    $[17] = listId;
    $[18] = onNavigate;
    $[19] = orphans;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <section className="mt-1 border-t border-tertiary">{t8}{t9}</section>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
const EmptyState = () => {
  const $ = _c(13);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.changes"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="size-9 rounded-full bg-accent/10 flex items-center justify-center"><IcoSparkles24 color="accent" /></span>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t(_temp17);
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <p className="text-body-3 text-secondary">{t2}</p>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t(_temp18);
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <p className="text-caption-2 text-tertiary">{t4}</p>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <div className="h-full flex flex-col items-center justify-center gap-2 px-8 text-center">{t1}{t3}{t5}</div>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
export default ChangesPanel;
function _temp($) {
  return $.summaryOne;
}
function _temp2($_0) {
  return $_0.summaryMany;
}
function _temp3($_1) {
  return $_1.dismissAll;
}
function _temp4($_2) {
  return $_2.acceptAll;
}
function _temp5($) {
  return $.badges.rules;
}
function _temp6($_0) {
  return $_0.badges.decision;
}
function _temp7($_1) {
  return $_1.badges.task;
}
function _temp8($) {
  return $.inputs;
}
function _temp9($_0) {
  return $_0.outputs;
}
function _temp10($_1) {
  return $_1.norms;
}
function _temp11($_2) {
  return $_2.skills;
}
function _temp12($_3) {
  return $_3.newStep;
}
function _temp13($_4) {
  return $_4.untitled;
}
function _temp14(expanded) {
  return !expanded;
}
function _temp15($) {
  return $.outOfDateOne;
}
function _temp16($_0) {
  return $_0.outOfDateMany;
}
function _temp17($) {
  return $.empty;
}
function _temp18($_0) {
  return $_0.emptyHint;
}